/** Datas no painel: `date` do banco vem como "AAAA-MM-DD" (sem fuso); `timestamptz` como ISO. */

export const APP_TIME_ZONE = "America/Bahia";

const dateTime = new Intl.DateTimeFormat("pt-BR", {
  timeZone: APP_TIME_ZONE,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
const monthNames = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const monthLong = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

/** "2026-09-27" (ou ISO) → "27/09/2026". Não passa por Date para não trocar o dia por fuso. */
export function formatDate(value: string | null | undefined, fallback = ""): string {
  if (!value) return fallback;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return fallback;
  if (value.length > 10 && value.includes("T")) {
    return dateTime.format(new Date(value)).slice(0, 10);
  }
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function formatDateTime(value: string | null | undefined, fallback = ""): string {
  if (!value) return fallback;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? fallback : dateTime.format(d).replace(",", "");
}

/** "2026-09-01" → "set/2026"; long → "setembro de 2026". */
export function formatMonth(value: string | null | undefined, long = false): string {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})/.exec(value);
  if (!m) return "";
  const i = Number(m[2]) - 1;
  return long ? `${monthLong[i]} de ${m[1]}` : `${monthNames[i]}/${m[1]}`;
}

/** Hoje no fuso da operação, "AAAA-MM-DD". */
export function todayIso(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
  return parts;
}

export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

export function monthStartIso(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

export function addMonthsIso(iso: string, months: number): string {
  const [y, m] = iso.split("-").map(Number) as [number, number];
  const dt = new Date(Date.UTC(y, m - 1 + months, 1));
  return dt.toISOString().slice(0, 10);
}

/** Diferença em dias entre duas datas "AAAA-MM-DD" (b − a). */
export function diffDays(a: string, b: string): number {
  const pa = Date.UTC(Number(a.slice(0, 4)), Number(a.slice(5, 7)) - 1, Number(a.slice(8, 10)));
  const pb = Date.UTC(Number(b.slice(0, 4)), Number(b.slice(5, 7)) - 1, Number(b.slice(8, 10)));
  return Math.round((pb - pa) / 86_400_000);
}

const rtf = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });
export function formatRelative(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return "";
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.round((t - now.getTime()) / 1000);
  const abs = Math.abs(s);
  if (abs < 60) return rtf.format(s, "second");
  if (abs < 3600) return rtf.format(Math.round(s / 60), "minute");
  if (abs < 86_400) return rtf.format(Math.round(s / 3600), "hour");
  if (abs < 86_400 * 30) return rtf.format(Math.round(s / 86_400), "day");
  return formatDate(value);
}

const hourMinute = new Intl.DateTimeFormat("pt-BR", { timeZone: APP_TIME_ZONE, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

/** Instante ISO → "14:30" no fuso da operação (ou no fuso informado). */
export function formatTime(value: string | null | undefined, timeZone: string = APP_TIME_ZONE, fallback = ""): string {
  if (!value) return fallback;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  const f =
    timeZone === APP_TIME_ZONE
      ? hourMinute
      : new Intl.DateTimeFormat("pt-BR", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return f.format(d);
}

/** Deslocamento do fuso (minutos, leste positivo) num instante. */
function tzOffsetMinutes(at: Date, timeZone: string): number {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
    })
      .formatToParts(at)
      .map((x) => [x.type, x.value]),
  );
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
  return Math.round((asUtc - at.getTime()) / 60000);
}

/**
 * Data "AAAA-MM-DD" + hora "HH:MM" de parede num fuso → instante ISO em UTC (para `timestamptz`).
 * Devolve "" quando a data ou a hora estão incompletas.
 */
export function zonedIso(date: string, time: string, timeZone: string = APP_TIME_ZONE): string {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const tm = /^(\d{2}):(\d{2})$/.exec(time);
  if (!dm || !tm) return "";
  const guess = Date.UTC(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), Number(tm[1]), Number(tm[2]));
  let ts = guess - tzOffsetMinutes(new Date(guess), timeZone) * 60000;
  ts = guess - tzOffsetMinutes(new Date(ts), timeZone) * 60000;
  return new Date(ts).toISOString();
}

/** Instante ISO → "AAAA-MM-DD" no fuso da operação (ou no fuso informado). */
export function isoDateIn(value: string | null | undefined, timeZone: string = APP_TIME_ZONE): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}
